import axios from 'axios'

// Create axios instance with base configuration
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3001/api',
  headers: {
    'Content-Type': 'application/json'
  },
  // Required for HttpOnly refresh_token cookie to be sent to /auth/refresh
  withCredentials: true,
})

// ============================================
// In-memory token storage
// ============================================
// Access token is stored in module state (never in localStorage/sessionStorage).
// Cleared on page refresh — /auth/refresh rehydrates it via AuthContext on mount.

let currentAccessToken = null

// Callbacks registered by AuthContext to handle token lifecycle events
let onTokenRefreshed = null
let onSessionExpired = null

/**
 * Set the in-memory access token.
 * Called by AuthContext after login, refresh, or SSO exchange.
 */
export const setAccessToken = (token) => {
  currentAccessToken = token
}

/**
 * Get the current in-memory access token.
 */
export const getAccessToken = () => currentAccessToken

/**
 * Register callbacks for token lifecycle events.
 * Called once by AuthContext on mount.
 *
 * @param {function} onRefreshed - Called with { user, accessToken, idToken } on successful refresh
 * @param {function} onExpired - Called when refresh fails (user must log in again)
 */
export const setAuthCallbacks = (onRefreshed, onExpired) => {
  onTokenRefreshed = onRefreshed
  onSessionExpired = onExpired
}

// ============================================
// Emulation session support
// ============================================

let currentEmulationSessionId = null

export const setEmulationSessionId = (sessionId) => {
  currentEmulationSessionId = sessionId
}

export const getEmulationSessionId = () => {
  return currentEmulationSessionId
}

// ============================================
// Maintenance mode state
// ============================================

let maintenanceState = { active: false, message: null }
let maintenanceCallbacks = []

export const getMaintenanceState = () => {
  return { ...maintenanceState }
}

export const onMaintenanceChange = (callback) => {
  maintenanceCallbacks.push(callback)
  return () => {
    maintenanceCallbacks = maintenanceCallbacks.filter(cb => cb !== callback)
  }
}

// ============================================
// Request interceptor
// ============================================

api.interceptors.request.use(
  async (config) => {
    // E2E test mode: use token from localStorage instead of in-memory token
    if (import.meta.env.VITE_E2E_AUTH) {
      const token = localStorage.getItem('e2e_auth_token')
      if (token) config.headers.Authorization = `Bearer ${token}`
      return config
    }

    // Add access token from in-memory state
    if (currentAccessToken) {
      config.headers.Authorization = `Bearer ${currentAccessToken}`
    }

    // Add emulation session header if set
    if (currentEmulationSessionId) {
      config.headers['X-Emulation-Session-Id'] = currentEmulationSessionId
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// ============================================
// Response interceptor — error handling + 401 refresh retry
// ============================================

let isRefreshing = false
let refreshQueue = []

function processQueue(error, token = null) {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error)
    else resolve(token)
  })
  refreshQueue = []
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (error.response) {
      const { status, data } = error.response

      // Detect maintenance mode from 503 responses
      if (status === 503 && data.code === 'MAINTENANCE_MODE') {
        maintenanceState = { active: true, message: data.message || null }
        maintenanceCallbacks.forEach(cb => {
          try { cb({ ...maintenanceState }) } catch (e) { console.error('Maintenance callback error:', e) }
        })
      }

      // Handle demo expired
      if (status === 403 && data.code === 'DEMO_EXPIRED') {
        console.debug('Demo trial expired for this tenant:', data.message || 'DEMO_EXPIRED')
      }

      // Only log unexpected server errors (5xx), not client errors (4xx)
      if (status >= 500) {
        console.error('Server error:', data.message || 'An internal server error occurred')
      }

      // Automatic token refresh on 401 — skip for auth endpoints to avoid infinite loops
      const url = originalRequest?.url || ''
      const isAuthEndpoint = url.includes('/auth/refresh') ||
                             url.includes('/auth/login') ||
                             url.includes('/auth/logout')

      if (status === 401 && !originalRequest._retry && !isAuthEndpoint && !import.meta.env.VITE_E2E_AUTH) {
        if (isRefreshing) {
          // Queue this request until the ongoing refresh completes
          return new Promise((resolve, reject) => {
            refreshQueue.push({ resolve, reject })
          }).then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            return api(originalRequest)
          })
        }

        originalRequest._retry = true
        isRefreshing = true

        try {
          const refreshResponse = await axios.post(
            `${api.defaults.baseURL}/auth/refresh`,
            {},
            { withCredentials: true }
          )

          const { accessToken, user, idToken } = refreshResponse.data
          setAccessToken(idToken)  // API Gateway authorizer validates ID tokens

          if (onTokenRefreshed) {
            onTokenRefreshed({ user, accessToken, idToken })
          }

          processQueue(null, idToken)
          originalRequest.headers.Authorization = `Bearer ${idToken}`
          return api(originalRequest)
        } catch (refreshError) {
          processQueue(refreshError)
          setAccessToken(null)
          if (onSessionExpired) {
            onSessionExpired()
          }
          return Promise.reject(formatError(error))
        } finally {
          isRefreshing = false
        }
      }

      return Promise.reject(formatError(error))
    } else if (error.request) {
      console.error('No response from server:', error.message)
      return Promise.reject({
        status: 0,
        message: 'Network error - could not connect to server',
        data: null
      })
    } else {
      console.error('Request error:', error.message)
      return Promise.reject({
        status: -1,
        message: error.message,
        data: null
      })
    }
  }
)

function formatError(error) {
  const { status, data } = error.response
  return {
    status,
    message: data.error || data.message || error.message,
    data: data
  }
}

export default api
